import { IconLibraryIcon, MenuItemInfo } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { IsNlpEnabled } from '~src/lens/utils/LensUtil';
import { isMetricsEnabled } from '~src/metrics/util/MetricUtils';
import { contentCountsRequest } from '~src/spacesPlatform/actions/ContentCountsActions';
import { PanelItemCount } from '~src/spacesPlatform/components/Explorer/widgets/ExplorerDropdownPanelItems';
import {
  ExplorerListMenuPanel,
  ExplorerListMenuPanelDispatchProps,
  ExplorerListMenuPanelOwnProps,
  ExplorerListMenuPanelStateProps
} from '~src/spacesPlatform/components/Explorer/widgets/ExplorerListMenuPanel';
import { selectContentCounts, selectContentCountsUpToDate } from '~src/spacesPlatform/selectors/ContentCountsSelector';
import { selectSiteSpaceContentType } from '~src/spacesPlatform/selectors/ContentFilterSelectors';
import { ISpaceInfoTypeItemCount } from '~src/spacesPlatform/state/ContentCountsState';

import { AppState } from '../../../bootstrap/redux/state/AppState';
import { Messages } from '../../../codegen/Localize';
import { selectFlowsOnSiteEnabled } from '../../../flow/selectors/FlowSelectors';
import { getMixedContentInOrder } from '../../../spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsLists';
import { SITE_SPACE_EXPLORE, SiteSpaceContentType } from '../../../spacesPlatform/state/SiteSpaceContentTypeState';

function isChecked(contentType: SiteSpaceContentType, currentContentType: SiteSpaceContentType): boolean {
  return currentContentType === contentType ||
    (contentType === 'datasource' && currentContentType === 'dataconnection');
}

function getMenuItems(
  currentContentType: SiteSpaceContentType,
  contentCounts: ISpaceInfoTypeItemCount<'site'>,
  contentCountsUpToDate: boolean,
  flowsEnabled: boolean,
): MenuItemInfo[] {

  let menuItems: MenuItemInfo[] = [];
  const checked = isChecked(SITE_SPACE_EXPLORE, currentContentType);
  menuItems.push({
    value: SITE_SPACE_EXPLORE,
    disabled: false,
    checked,
    displayValue: Messages.siteFilterByTypeDropdown_TopLevelProjects(),
    annotation: ((/* intentionally unused */ props) => <PanelItemCount
      displayCount
      countUpToDate={contentCountsUpToDate}
      count={contentCountsUpToDate && contentCounts ? contentCounts['explore'] : undefined}
    />) as IconLibraryIcon,
  });

  menuItems = menuItems.concat(_.compact(getMixedContentInOrder().map((contentDefinition) => {
    const checked = isChecked(contentDefinition.itemType, currentContentType);
    if (
      (contentDefinition.itemType !== 'flow' || flowsEnabled) &&
      (contentDefinition.itemType !== 'lens' || IsNlpEnabled()) &&
      (contentDefinition.itemType !== 'metric' || isMetricsEnabled()) &&
      (contentDefinition.itemType !== 'virtualConnection' || FeatureFlags.publishedConnectionContentManagementEnabled) &&
      (contentDefinition.itemType !== 'predictiveModel' || FeatureFlags.prometheusPredictiveModelEnabled) &&
      (contentDefinition.itemType !== 'database') &&
      (contentDefinition.itemType !== 'table')) {
      return {
        value: contentDefinition.itemType,
        disabled: false,
        checked,
        displayValue: contentDefinition.siteFilterByTypeDropdownName(),
        annotation: ((/* intentionally unused */ props) => <PanelItemCount
          displayCount
          countUpToDate={contentCountsUpToDate}
          count={contentCountsUpToDate && contentCounts ? contentCounts[contentDefinition.itemType] : undefined}
        />) as IconLibraryIcon,
      };
    }
  })));
  return menuItems;
}

const getMenuItemsWrapper = defaultMemoize(getMenuItems);

const mapStateToProps: MapStateToProps<ExplorerListMenuPanelStateProps> =
  (state: AppState) => {
    const currentContentType = selectSiteSpaceContentType(state);
    const contentCounts = selectContentCounts<'site'>(state, 'site');
    const contentCountsUpToDate = selectContentCountsUpToDate(state);
    const flowsEnabled = selectFlowsOnSiteEnabled(state);
    const menuItems = getMenuItemsWrapper(currentContentType, contentCounts, contentCountsUpToDate, flowsEnabled);
    return {
      menuItems
    };
  };

const mapDispatchToProjectProps: MapDispatchToProps<ExplorerListMenuPanelDispatchProps> =
  (dispatch) => bindActionCreators({
    onComponentDidUpdate: contentCountsRequest
  },
  dispatch);

export const ConnectedSiteLevelFilterMenu: React.ComponentClass<ExplorerListMenuPanelOwnProps> =
  connect(mapStateToProps, mapDispatchToProjectProps)(ExplorerListMenuPanel);
